import { Button, Checkbox, Label, Modal, TextInput } from "flowbite-react";
import { useEffect, useState } from "react";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { useForm } from "react-hook-form";
import { Agent } from "@/sharedTypes";
import { agentRoles, JOB_ROLES, agentRoleLabels } from "@/constants/agent";
import GroupSelect from "@/components/agents/GroupSelect";
import WorkShiftSelect from "@/components/workShifts/WorkShiftSelect";
import { useTranslation } from "react-i18next";
import { DISPLAY_NAME_RE, USERNAME_RE } from "@/constants/validation";

const extractJobRole = (raw: Agent["job_roles"]): number | "" => {
  if (!Array.isArray(raw) || raw.length === 0) return "";
  const first = raw[0];
  const role = typeof first === "object" && first !== null ? first.role : first;
  return typeof role === "number" ? role : "";
};

interface AgentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  mode?: "create" | "edit";
  initialData?: Agent;
  errorMessage?: string;
}

export default function AgentFormDialog({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialData,
  errorMessage,
}: AgentFormDialogProps) {
  const { t } = useTranslation(["agent", "common"]);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<any>();

  useEffect(() => {
    if (open) {
      if (initialData) {
        reset({
          agent_username: initialData.agent_username,
          agent_email: initialData.agent_email,
          display_name: initialData.display_name,
          agent_role: initialData.agent_role ?? "",
          shift_id: initialData.shift_id ?? "",
          agent_password: "",
          group_ids: initialData.groups?.map((g) => g.group_id) ?? [],
          job_role: extractJobRole(initialData.job_roles),
          is_admin: false,
        });
      } else {
        reset({
          agent_username: "",
          agent_email: "",
          agent_password: "",
          display_name: "",
          agent_role: agentRoles.agent,
          shift_id: "",
          group_ids: [] as number[],
          job_role: agentRoles.backoffice,
          is_admin: false,
        });
      }
    }
  }, [open, initialData, reset]);

  const handleGroupChange = (value: number[]) => {
    setValue("group_ids", value);
  };

  const isAiAgent = mode === "edit" && initialData?.agent_role === agentRoles.ai_agent;
  const selectedJobRole: number | "" = watch("job_role") ?? "";
  const setJobRole = (role: number) => {
    setValue("job_role", role, { shouldValidate: true });
  };

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>{mode === "edit" ? t("agent:editAgentTitle") : t("agent:createAgentTitle")}</Modal.Header>
      <Modal.Body>
        <form
          onSubmit={handleSubmit((data) => {
            if (isAiAgent) {
              const { job_role, agent_username, agent_email, agent_password, agent_role, ...rest } = data;
              onSubmit(rest);
              return;
            }
            const role: number | "" = data.job_role ?? "";
            if (typeof role !== "number") {
              return; // blocks submit; helper text already visible
            }
            // Backend expects an array; we always send exactly one role.
            const { job_role, ...rest } = data;
            onSubmit({ ...rest, job_roles: [role] });
          })}
          className="space-y-4"
        >
          {isAiAgent && (
            <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
              {t("agent:aiAgentLockedHint")}
            </p>
          )}
          <div>
            <Label htmlFor="agent_username" value={t("agent:agentUsername")} />
            <TextInput
              id="agent_username"
              placeholder={t("agent:enterAgentUsername")}
              disabled={isAiAgent}
              {...register("agent_username", {
                required: t("agent:agentUsernameRequired"),
                minLength: { value: 2, message: t("agent:usernamePattern") },
                maxLength: { value: 40, message: t("agent:usernamePattern") },
                pattern: { value: USERNAME_RE, message: t("agent:usernamePattern") },
              })}
              color={errors.agent_username ? "failure" : undefined}
              helperText={errors.agent_username?.message as string}
            />
          </div>

          <div>
            <Label htmlFor="display_name" value={t("agent:displayName")} />
            <TextInput
              id="display_name"
              placeholder={t("agent:enterDisplayName")}
              {...register("display_name", {
                required: t("agent:displayNameRequired"),
                minLength: { value: 2, message: t("agent:displayNamePattern") },
                maxLength: { value: 50, message: t("agent:displayNamePattern") },
                pattern: { value: DISPLAY_NAME_RE, message: t("agent:displayNamePattern") },
              })}
              color={errors.display_name ? "failure" : undefined}
              helperText={errors.display_name?.message as string}
            />
          </div>

          <div>
            <Label htmlFor="agent_email" value={t("agent:agentEmail")} />
            <TextInput
              id="agent_email"
              type="email"
              placeholder={t("agent:enterAgentEmail")}
              disabled={isAiAgent}
              {...register("agent_email", {
                required: t("agent:agentEmailRequired"),
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: t("agent:invalidEmailFormat"),
                },
              })}
              color={errors.agent_email ? "failure" : undefined}
              helperText={errors.agent_email?.message as string}
            />
          </div>

          <div>
            <Label htmlFor="agent_password" value={t("agent:password")} />
            <div className="relative">
              <TextInput
                id="agent_password"
                type={showPassword ? "text" : "password"}
                placeholder={t("agent:enterAgentPassword")}
                disabled={isAiAgent}
                {...register("agent_password", {
                  required: mode === "create" ? t("agent:passwordRequired") : false
                })}
                color={errors.agent_password ? "failure" : undefined}
                helperText={errors.agent_password?.message as string}
              />
              <button
                type="button"
                className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? <HiEyeOff className="h-5 w-5" /> : <HiEye className="h-5 w-5" />}
              </button>
            </div>
          </div>


          <WorkShiftSelect
            value={watch("shift_id")}
            onChange={(val) => setValue("shift_id", val)}
            error={errors.shift_id?.message as string}
          />

          {!isAiAgent && (
          <div>
            <Label value={t("agent:jobRole.label", { defaultValue: "Job Role" })} />
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t("agent:jobRoleHint", {
                defaultValue: "Pick the role this agent will fulfil.",
              })}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {JOB_ROLES.map((role) => {
                const checked = selectedJobRole === role;
                return (
                  <label
                    key={role}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      checked
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                        : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    <input
                      type="radio"
                      name="job_role"
                      checked={checked}
                      onChange={() => setJobRole(role)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {t(`agent:jobRole.${agentRoleLabels[role].toLowerCase()}`, {
                        defaultValue: agentRoleLabels[role],
                      })}
                    </span>
                  </label>
                );
              })}
            </div>
            {selectedJobRole === "" && (
              <p className="mt-1 text-xs text-red-500">
                {t("agent:jobRoleRequired", {
                  defaultValue: "A job role is required.",
                })}
              </p>
            )}
          </div>
          )}

          <div className="space-y-3">
            <GroupSelect
              value={watch("group_ids") || []}
              onChange={handleGroupChange}
              error={errors.group_ids?.message as string}
            />

            {mode === "create" && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={Boolean(watch("is_admin"))}
                  onChange={(e) => setValue("is_admin", e.target.checked)}
                />
                <Label value={t("agent:isAdmin")} />
              </div>
            )}
          </div>

          {errorMessage && (
            <p className="text-sm text-red-600">{errorMessage}</p>
          )}

          <div className="flex justify-end gap-2">
            <Button color="failure" onClick={onClose} type="button">
              {t("common:cancel")}
            </Button>
            <Button type="submit">
              {mode === "edit" ? t("common:saveChanges") : t("agent:createAgentTitle")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
